import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import styled from '@mui/material/styles/styled';
import { toDisplayString } from '../../../utils/base';

const ChartContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    width: '100%',
}));

const SingleChartContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    width: '100%',
    position: 'relative',
}));

const ChartTitle = styled(Typography)(({ theme }) => ({
    fontSize: '14px',
    fontWeight: 600,
    color: theme.palette.figma.white[100],
    letterSpacing: '-0.42px',
}));

const ChartWrapper = styled(Box)(({ theme }) => ({
    position: 'relative',
    width: '100%',
    height: '200px',
    padding: '30px 20px 10px 40px', // 상단에 X축 라벨 공간, 좌측에 Y축 라벨 공간, 오른쪽 여백 추가
    overflow: 'hidden', // 차트가 컨테이너를 벗어나지 않도록
}));

const YAxisContainer = styled(Box)(({ theme }) => ({
    position: 'absolute',
    left: 0,
    top: '30px', // X축 라벨 공간 확보
    bottom: 0,
    width: '30px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    paddingRight: '5px',
    zIndex: 2,
}));

const YAxisLabel = styled(Typography)(({ theme }) => ({
    fontSize: '12px',
    fontWeight: 400,
    color: theme.palette.figma.white[60],
    letterSpacing: '-0.36px',
}));

const XAxisContainer = styled(Box)(({ theme }) => ({
    position: 'absolute',
    left: '40px',
    right: '20px', // 오른쪽 여백 추가
    top: 0,
    height: '30px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end', // 하단 정렬 (라벨이 아래로 향하도록)
    paddingBottom: '5px',
    overflow: 'visible', // 라벨이 잘리지 않도록 visible로 변경
    zIndex: 2,
}));

const XAxisLabel = styled(Typography)(({ theme }) => ({
    fontSize: '12px',
    fontWeight: 400,
    color: theme.palette.figma.white[60],
    letterSpacing: '-0.36px',
    whiteSpace: 'nowrap',
    position: 'absolute',
    left: 0,
}));

const GridLine = styled(Box)(({ theme, position, chartHeight = 160 }) => ({
    position: 'absolute',
    left: '40px',
    right: '20px', // 오른쪽 여백 추가
    height: '1px',
    // position: 100 = 최소값(위쪽), position: 0 = 최대값(아래쪽)
    // X축 라벨이 상단에 있으므로 30px 아래부터 시작
    top: `calc(30px + ${(100 - position)}% * ${chartHeight}px / 100)`,
    backgroundColor: theme.palette.figma.white[20],
    zIndex: 1,
}));

const LinePath = styled('path')(({ theme, color }) => ({
    fill: 'none',
    stroke: color || theme.palette.figma.time,
    strokeWidth: 0.5,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
}));

const DataPoint = styled('circle')(({ theme, color }) => ({
    fill: color || theme.palette.figma.time,
    stroke: 'none',
    r: 1,
}));

const RankingLineChart = ({
    charts = [], // [{ title: '6학년2반', data: [{ month: '2024-11', value: 8 }, ...], color: '#...' }, ...]
    maxValue = null, // Y축 최대값 (null이면 자동 계산)
}) => {
    const theme = useTheme();

    // 현재 월부터 과거 12개월 생성
    const generateMonthLabels = () => {
        const today = new Date();
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth() + 1; // 1-12

        const labels = [];
        for (let i = 0; i < 12; i++) {
            let year = currentYear;
            let month = currentMonth - i;

            if (month <= 0) {
                month += 12;
                year -= 1;
            }

            labels.push({
                label: `${month}월`,
                year: year,
                month: month,
                key: `${year}-${String(month).padStart(2, '0')}`
            });
        }

        return labels.reverse(); // 과거부터 현재 순서로
    };

    const monthLabels = generateMonthLabels();

    // 각 차트에 대해 최대값 계산
    const calculateMaxValue = (data) => {
        if (maxValue !== null) return maxValue;
        if (!data || data.length === 0) return 1;
        const values = data.map(d => d.value || 0);
        const max = Math.max(...values);
        if (max === 0) return 1;
        // 최대값을 적절한 단위로 반올림
        if (max < 10) return Math.ceil(max / 2) * 2;
        if (max < 100) return Math.ceil(max / 10) * 10;
        if (max < 1000) return Math.ceil(max / 100) * 100;
        return Math.ceil(max / 500) * 500;
    };

    const renderChart = (chart, index) => {
        const { title, data = [], color = theme.palette.figma.time } = chart;
        const chartMaxValue = calculateMaxValue(data);
        const chartHeight = 160; // 실제 차트 영역 높이
        const chartWidth = 100; // viewBox 기준 너비 (100%로 스케일링됨)

        // 데이터를 월 순서대로 정렬 (배열 인덱스로 매칭 - 다국어 호환)
        // data와 monthLabels는 동일 순서(과거→현재)이므로 인덱스로 매칭
        const sortedData = monthLabels.map((monthLabel, monthIndex) => {
            const monthData = data[monthIndex];
            return {
                ...(monthData || { month: monthLabel.label, value: 0 }),
                monthIndex,
                originalMonthLabel: monthLabel
            };
        });

        // Y축 최소값은 1
        const minValue = 1;

        // Y축 그리드 라인 위치 (최소값, 중간, 최대값) - 정수일 때만 표시
        let rawValues = [];

        if (chartMaxValue === 1) {
            // 최대값이 1인 경우 최소값 1만 표시
            rawValues = [
                { value: minValue, position: 100 }
            ];
        } else {
            // 최대값이 1보다 큰 경우
            rawValues = [
                { value: minValue, position: 100 },
                { value: (minValue + chartMaxValue) / 2, position: 50 },
                { value: chartMaxValue, position: 0 }
            ];
        }

        // 정수인 값만 필터링하고 중복 제거
        const yAxisData = rawValues.filter(item => {
            return Number.isInteger(item.value);
        });

        // 중복 제거 (같은 value를 가진 항목 제거)
        const uniqueYAxisData = yAxisData.filter((item, index, self) =>
            index === self.findIndex(t => t.value === item.value)
        );

        const yAxisValues = uniqueYAxisData.map(item => item.value);
        const gridPositions = uniqueYAxisData.map(item => item.position);

        // SVG 경로 생성 (viewBox 기준으로 0-100 범위 사용)
        // Y축 최소값은 1이므로 범위는 (chartMaxValue - minValue)
        const range = chartMaxValue - minValue;

        // 0이 아닌 값들만 필터링 (그리기용)
        const dataToDraw = sortedData.filter(item => item.value !== 0 && item.value !== null);

        // 각 데이터 포인트의 x 위치 계산 (전체 monthLabels 기준)
        // 0이 아닌 값들만 사용하되, 각 포인트의 X 위치는 원래 월의 인덱스를 사용
        const pathData = dataToDraw.map((point, idx) => {
            // 원래 월 인덱스 사용 (monthIndex가 저장되어 있음)
            const monthIndex = point.monthIndex !== undefined ? point.monthIndex :
                monthLabels.findIndex(ml =>
                    ml.label === point.month || ml.key === point.month ||
                    (point.month && point.month.startsWith && point.month.startsWith(ml.key))
                );
            const x = monthIndex >= 0 ? (monthIndex / (monthLabels.length - 1)) * chartWidth : 0;
            // 반전된 Y축: 값이 작을수록 위에, 최소값 1 고려
            // SVG에서 y=0은 위쪽, y=chartHeight는 아래쪽
            // 값이 1 (최소값, 1등)일 때 → y = 0 (위쪽)
            // 값이 최대값일 때 → y = chartHeight (아래쪽)
            const pointValue = Math.max(point.value || 0, minValue); // 최소값 이하는 1로 처리
            // range가 0인 경우(최대값=최소값=1) 처리
            const y = range > 0 ? ((pointValue - minValue) / range) * chartHeight : 0;
            return `${idx === 0 ? 'M' : 'L'} ${x} ${isNaN(y) ? 0 : y}`;
        }).join(' ');

        return (
            <SingleChartContainer key={index}>
                <ChartTitle>{title}</ChartTitle>
                <ChartWrapper>
                    {/* 그리드 라인 - 정수인 값만 표시 */}
                    {gridPositions.map((position, idx) => (
                        <GridLine key={idx} position={position} />
                    ))}

                    {/* Y축 라벨 - 정수인 값만 표시 */}
                    {yAxisValues.length > 0 && (
                        <YAxisContainer>
                            {yAxisValues.map((value, idx) => (
                                <YAxisLabel key={idx}>
                                    {value.toLocaleString()}
                                </YAxisLabel>
                            ))}
                        </YAxisContainer>
                    )}

                    {/* X축 라벨 (sortedData의 month 사용 - 다국어 지원) */}
                    <XAxisContainer>
                        {sortedData.map((item, idx) => {
                            const position = (idx / (monthLabels.length - 1)) * 100;
                            const isFirst = idx === 0;
                            const isLast = idx === monthLabels.length - 1;
                            const displayLabel = item.month || item.originalMonthLabel?.label || item.originalMonthLabel?.key || '';
                            return (
                                <XAxisLabel
                                    key={item.originalMonthLabel?.key || idx}
                                    sx={{
                                        left: `${position}%`,
                                        transform: isFirst ? 'translateX(0)' : isLast ? 'translateX(-100%)' : 'translateX(-50%)',
                                    }}
                                >
                                    {toDisplayString(displayLabel)}
                                </XAxisLabel>
                            );
                        })}
                    </XAxisContainer>

                    {/* SVG 차트 */}
                    <Box
                        sx={{
                            position: 'absolute',
                            left: '40px',
                            top: '30px', // X축 라벨 공간 확보
                            right: '20px', // 오른쪽 여백 추가
                            bottom: '10px',
                            overflow: 'hidden', // 차트가 벗어나지 않도록
                        }}
                    >
                        <svg
                            width="100%"
                            height="100%"
                            viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                            preserveAspectRatio="none"
                            style={{
                                display: 'block',
                            }}
                        >
                            {/* 라인 */}
                            <LinePath d={pathData} color={color} />

                            {/* 데이터 포인트 - 0이 아닌 값만 표시 */}
                            {dataToDraw.map((point, idx) => {
                                // 원래 월 인덱스 사용 (monthIndex가 저장되어 있음)
                                const monthIndex = point.monthIndex !== undefined ? point.monthIndex :
                                    monthLabels.findIndex(ml =>
                                        ml.label === point.month || ml.key === point.month ||
                                        (point.month && point.month.startsWith && point.month.startsWith(ml.key))
                                    );
                                const x = monthIndex >= 0 ? (monthIndex / (monthLabels.length - 1)) * chartWidth : 0;
                                // Y축 최소값은 1이므로 범위는 (chartMaxValue - minValue)
                                // SVG에서 y=0은 위쪽, y=chartHeight는 아래쪽
                                // 값이 1 (최소값, 1등)일 때 → y = 0 (위쪽)
                                // 값이 최대값일 때 → y = chartHeight (아래쪽)
                                const pointValue = Math.max(point.value || 0, minValue); // 최소값 이하는 1로 처리
                                const range = chartMaxValue - minValue;
                                // range가 0인 경우(최대값=최소값=1) 처리
                                const y = range > 0 ? ((pointValue - minValue) / range) * chartHeight : 0;
                                const validY = isNaN(y) ? 0 : y;
                                return (
                                    <DataPoint
                                        key={idx}
                                        cx={x}
                                        cy={validY}
                                        color={color}
                                    />
                                );
                            })}
                        </svg>
                    </Box>
                </ChartWrapper>
            </SingleChartContainer>
        );
    };

    return (
        <ChartContainer>
            {charts.map((chart, index) => renderChart(chart, index))}
        </ChartContainer>
    );
};

export default RankingLineChart;

